/**
 * PayDepositModal - Gold-standard modal for online deposit payment via Finix
 * 
 * Triggered after estimate approval when:
 * - deposit_required=true
 * - tenant has payments enabled
 * 
 * Uses existing Finix hosted card capture flow.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import { CheckCircle, SpinnerGap, CreditCard, Warning } from "phosphor-react";
import { formatCurrency } from "../../utils/formatters";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Finix SDK CDN - Official URL from docs.finix.com
// CRITICAL: URL must be /v/2/ NOT /v2/ or /v1/
const FINIX_SDK_URL = "https://js.finix.com/v/2/finix.js";
const FORM_INIT_TIMEOUT = 15000;

export default function PayDepositModal({
  isOpen,
  onOpenChange,
  depositInvoice,
  estimateNumber,
  onSuccess,
  onSkip,
}) {
  // Payment states
  const [step, setStep] = useState("loading"); // loading, form, processing, success, error
  const [error, setError] = useState(null);
  
  // Finix state
  const [tokenizationConfig, setTokenizationConfig] = useState(null);
  const [finixFormReady, setFinixFormReady] = useState(false);
  const finixFormRef = useRef(null);
  const finixPaymentFormInstance = useRef(null);
  const finixTimeoutRef = useRef(null);
  
  // Load Finix SDK
  const loadFinixSdk = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.Finix) {
        resolve();
        return;
      }
      
      const existingScript = document.querySelector(`script[src="${FINIX_SDK_URL}"]`);
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve());
        return;
      }
      
      const script = document.createElement("script");
      script.src = FINIX_SDK_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load payment form"));
      document.head.appendChild(script);
    });
  }, []);
  
  // Fetch tokenization config
  const fetchTokenizationConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API}/billing/tokenization-bootstrap`, {
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (!data.ok || data.provider !== "finix") {
        throw new Error(data.message || "Payment setup unavailable");
      }
      
      setTokenizationConfig(data);
      await loadFinixSdk();
      
    } catch (err) {
      console.error("[PayDeposit] Config error:", err);
      setError(err.message || "Unable to load payment form");
      setStep("error");
    }
  }, [loadFinixSdk]);
  
  // Initialize Finix form
  const initializeFinixForm = useCallback(() => {
    if (!tokenizationConfig || !window.Finix || !finixFormRef.current) {
      return;
    }
    
    if (finixPaymentFormInstance.current) {
      return; // Already initialized
    }
    
    try {
      const environment = tokenizationConfig.environment || "sandbox";
      const applicationId = tokenizationConfig.application_id;
      
      finixFormRef.current.innerHTML = "";
      
      finixTimeoutRef.current = setTimeout(() => {
        setError("Payment form couldn't load. Please try again.");
        setStep("error");
      }, FORM_INIT_TIMEOUT);
      
      const form = window.Finix.PaymentForm(
        finixFormRef.current,
        environment,
        applicationId,
        {
          paymentMethods: ["card"],
          defaultCountry: "US",
          showAddress: false,
          styles: {
            default: {
              color: "#18181b",
              fontSize: "16px",
              fontFamily: "system-ui, -apple-system, sans-serif",
              "::placeholder": { color: "#a1a1aa" },
            },
            error: { color: "#dc2626" },
          },
          onUpdate: (state, binInfo, hasErrors) => {
            if (!finixFormReady && finixTimeoutRef.current) {
              clearTimeout(finixTimeoutRef.current);
              finixTimeoutRef.current = null;
              setFinixFormReady(true);
              setStep("form");
            }
          },
        }
      );
      
      finixPaymentFormInstance.current = form;
      
    } catch (err) {
      console.error("[PayDeposit] Form init error:", err);
      setError("Failed to initialize payment form");
      setStep("error");
    }
  }, [tokenizationConfig, finixFormReady]);
  
  // Cleanup
  const cleanupFinixForm = useCallback(() => {
    if (finixTimeoutRef.current) {
      clearTimeout(finixTimeoutRef.current);
      finixTimeoutRef.current = null;
    }
    
    if (finixPaymentFormInstance.current) {
      try {
        finixPaymentFormInstance.current.destroy?.();
      } catch (e) {}
      finixPaymentFormInstance.current = null;
    }
    
    if (finixFormRef.current) {
      finixFormRef.current.innerHTML = "";
    }
    
    setFinixFormReady(false);
  }, []);
  
  // Handle payment submission
  const handleSubmit = async () => {
    if (!finixPaymentFormInstance.current || !depositInvoice) {
      return;
    }
    
    setStep("processing");
    setError(null);
    
    try {
      // Tokenize card
      const tokenResponse = await new Promise((resolve) => {
        finixPaymentFormInstance.current.submit("", (err, response) => {
          resolve({ err, response });
        });
      });
      
      if (tokenResponse.err) {
        const errorMsg = typeof tokenResponse.err === "string" 
          ? tokenResponse.err 
          : tokenResponse.err?.message || "Card validation failed";
        throw new Error(errorMsg);
      }
      
      const tokenData = tokenResponse.response?.data || {};
      const paymentInstrumentId = tokenData.id;
      
      if (!paymentInstrumentId) {
        throw new Error("Unable to process card. Please try again.");
      }
      
      // Record payment against the deposit invoice
      // Using the standard record-payment endpoint which handles DEPOSIT invoices
      const paymentResponse = await fetch(
        `${API}/invoices/${depositInvoice.invoice_id}/record-payment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            amount: depositInvoice.total,
            method: "card",
            payment_instrument_id: paymentInstrumentId,
          }),
        }
      );
      
      if (!paymentResponse.ok) {
        const errData = await paymentResponse.json().catch(() => ({}));
        throw new Error(errData.detail || "Payment failed. Please try again.");
      }
      
      // Success!
      setStep("success");
      toast.success("Deposit paid successfully!");
      
      // Notify parent after brief delay
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 2000);
      
    } catch (err) {
      console.error("[PayDeposit] Payment error:", err);
      setError(err.message || "Payment failed. Please try again.");
      setStep("form");
    }
  };
  
  // Effects
  useEffect(() => {
    if (isOpen && !tokenizationConfig) {
      setStep("loading");
      fetchTokenizationConfig();
    }
  }, [isOpen, tokenizationConfig, fetchTokenizationConfig]);
  
  useEffect(() => {
    if (isOpen && tokenizationConfig && !finixFormReady) {
      initializeFinixForm();
    }
  }, [isOpen, tokenizationConfig, finixFormReady, initializeFinixForm]);
  
  useEffect(() => {
    if (!isOpen) {
      cleanupFinixForm();
      setStep("loading");
      setError(null);
    }
  }, [isOpen, cleanupFinixForm]);
  
  // Don't render if no invoice
  if (!depositInvoice) return null;
  
  const amount = depositInvoice.total || 0;
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="pay-deposit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            {step === "success" ? "Payment Complete" : "Pay Deposit"}
          </DialogTitle>
        </DialogHeader>
        
        <DialogBody>
          {/* Loading State */}
          {step === "loading" && (
            <div className="flex flex-col items-center justify-center py-8">
              <SpinnerGap size={40} className="animate-spin text-zinc-400 mb-4" />
              <p className="text-sm text-zinc-500">Loading payment form...</p>
            </div>
          )}
          
          {/* Error State */}
          {step === "error" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-md">
                <Warning size={24} className="text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Payment Unavailable</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
              <p className="text-sm text-zinc-500 text-center">
                You can pay the deposit later or ask your contractor for other payment options.
              </p>
            </div>
          )}
          
          {/* Form State */}
          {(step === "form" || step === "processing") && (
            <div className="space-y-4">
              {/* Amount Display */}
              <div className="bg-emerald-50 p-4 rounded-sm text-center">
                <p className="text-sm text-emerald-600">Deposit Amount</p>
                <p className="font-heading text-3xl font-bold text-emerald-700">
                  {formatCurrency(amount)}
                </p>
                {estimateNumber && (
                  <p className="text-xs text-emerald-500 mt-1">
                    For {estimateNumber}
                  </p>
                )}
              </div>
              
              {/* Card Entry */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  <CreditCard size={16} className="inline mr-1" />
                  Card Details
                </label>
                <div 
                  ref={finixFormRef}
                  className="border-2 border-zinc-200 rounded-md p-3 min-h-[120px] bg-white"
                  data-testid="finix-card-form"
                />
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <Warning size={16} className="text-red-600" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Success State */}
          {step === "success" && (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle size={40} className="text-emerald-600" weight="fill" />
              </div>
              <p className="text-lg font-medium text-zinc-800">Deposit Paid!</p>
              <p className="text-sm text-zinc-500 mt-1">
                {formatCurrency(amount)} has been processed
              </p>
              {estimateNumber && (
                <p className="text-xs text-zinc-400 mt-2">
                  For {estimateNumber}
                </p>
              )}
            </div>
          )}
        </DialogBody>
        
        <DialogFooter>
          {/* Loading/Error Footer */}
          {(step === "loading" || step === "error") && (
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                if (onSkip) onSkip();
              }}
              className="w-full border-2"
            >
              {step === "error" ? "Pay Later" : "Cancel"}
            </Button>
          )}
          
          {/* Form Footer */}
          {(step === "form" || step === "processing") && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  if (onSkip) onSkip();
                }}
                disabled={step === "processing"}
                className="flex-1 border-2"
              >
                Pay Later
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!finixFormReady || step === "processing"}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="confirm-deposit-payment-btn"
              >
                {step === "processing" ? (
                  <>
                    <SpinnerGap size={18} className="mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard size={18} className="mr-2" />
                    Pay {formatCurrency(amount)}
                  </>
                )}
              </Button>
            </>
          )}
          
          {/* Success Footer */}
          {step === "success" && (
            <Button
              onClick={() => {
                onOpenChange(false);
                if (onSuccess) onSuccess();
              }}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
